import { Injectable, inject } from '@angular/core';
import { CoreHttpClient } from '@bepatient/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Quizz, QuizzResponse } from './quizz.models';

@Injectable()
export class QuizzApiService {
  private http = inject(CoreHttpClient);

  loadQuizz(url: string): Observable<Quizz> {
    const split = url.split('/answers/');
    return this.http
      .get<any>(split[0], { withMobiCredentials: true })
      .pipe(map(res => this.quizzParser(res)));
  }

  saveQuizz(url: string, data: any): Observable<QuizzResponse> {
    const split = url.split('/answers/');
    return split.length === 2
      ? this.saveQuizzAnswers(url, data)
      : this.http
          .get<any>(`${url}/answers`, { withMobiCredentials: true })
          .pipe(
            map(res => res.items.filter(item => item.status === 'todo')),
            switchMap(todoItems =>
              todoItems.length > 0
                ? this.saveQuizzAnswers(
                    `${url}/answers/${todoItems[0].uuid}`,
                    data
                  )
                : this.createQuizzActivity(url).pipe(
                    switchMap(location => this.saveQuizzAnswers(location, data))
                  )
            )
          );
  }

  private quizzParser(data: any): Quizz {
    return {
      href: (data.links && data.links.self && data.links.self.href) || '',
      color: (data.meta && data.meta.color) || '#000',
      title: (data.meta && data.meta.title) || '',
      image: (data.meta && data.meta.thumbnail) || '',
      questions: (
        (data.meta && data.meta.model && data.meta.model.fields) ||
        []
      ).map(field => ({
        name: field.name || '',
        label: field.label || '',
        correctValue: field.correct_answer || '',
        selectedValue: void 0,
        rightAnswerText: field.msg_good_answer || '',
        wrongAnswerText: field.msg_bad_answer || '',
        answers: (Object.values(field.options || {}) as any[]).map(option => ({
          value: option.value || '',
          label: option.label || '',
          displayAsRight: false,
          displayAsWrong: false
        }))
      }))
    };
  }

  private quizzResponseParser(data: any): QuizzResponse {
    const getBox: any = (id: string) =>
      ((data.end_boxes as any[]) || []).find(box => box.class_id === id);

    const boxScore = getBox('box-quizz-result');
    const boxAnswers = getBox('box-quizz-answers');

    return {
      score: boxScore
        ? { title: boxScore.title || '', value: boxScore.result || 0 }
        : { title: '', value: 0 },
      answers: boxAnswers
        ? {
            title: boxAnswers.title,
            content: ((boxAnswers.answers as any[]) || []).map(answer => ({
              title: answer.title || '',
              value: answer.value || '',
              correct: answer.is_good_answer || false
            }))
          }
        : void 0
    };
  }

  private createQuizzActivity(url: string): Observable<string> {
    return this.http
      .post<any>(`${url}/answers`, {}, { withMobiCredentials: true })
      .pipe(map(res => res.location));
  }

  private saveQuizzAnswers(url: string, data: any): Observable<QuizzResponse> {
    return this.http
      .put<any>(url, data, { withMobiCredentials: true })
      .pipe(map(res => this.quizzResponseParser(res)));
  }

  private mockedQuizz(): Observable<Quizz> {
    return of<Quizz>({
      href: 'myHref',
      color: '#f00',
      title: 'Mon Quizz',
      image: '',
      questions: [
        {
          name: 'aaa',
          label: 'Question 1',
          correctValue: '0',
          selectedValue: void 0,
          rightAnswerText: 'Bien joué !!',
          wrongAnswerText: 'Oups..',
          answers: [
            {
              value: '0',
              label: 'Oui',
              displayAsRight: false,
              displayAsWrong: false
            },
            {
              value: '1',
              label: 'Non',
              displayAsRight: false,
              displayAsWrong: false
            },
            {
              value: '2',
              label: 'Non',
              displayAsRight: false,
              displayAsWrong: false
            }
          ]
        },
        {
          name: 'bbb',
          label: 'Question 2',
          correctValue: '1',
          selectedValue: void 0,
          rightAnswerText: 'Bien joué !!',
          wrongAnswerText: 'Oups..',
          answers: [
            {
              value: '0',
              label: 'Non',
              displayAsRight: false,
              displayAsWrong: false
            },
            {
              value: '1',
              label: 'Oui',
              displayAsRight: false,
              displayAsWrong: false
            },
            {
              value: '2',
              label: 'Non',
              displayAsRight: false,
              displayAsWrong: false
            }
          ]
        },
        {
          name: 'ccc',
          label: 'Question 3',
          correctValue: '2',
          selectedValue: void 0,
          rightAnswerText: 'Bien joué !!',
          wrongAnswerText: 'Oups..',
          answers: [
            {
              value: '0',
              label: 'Non',
              displayAsRight: false,
              displayAsWrong: false
            },
            {
              value: '1',
              label: 'Non',
              displayAsRight: false,
              displayAsWrong: false
            },
            {
              value: '2',
              label: 'Oui',
              displayAsRight: false,
              displayAsWrong: false
            }
          ]
        }
      ]
    });
  }
}
